import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { AnimalItem } from "../../../../mocks/animals";
import { useAnimals } from "../../../../hooks/useAnimals";
import { useCustomers } from "../../../../hooks/useCustomers";
import { usePayments } from "../../../../hooks/usePayments";
import {
  analyzeAnimalDeletion,
  deleteAnimalCascade,
} from "../../../../lib/cascadeOps";
import {
  getAnimalSalesType,
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../lib/animalLabels";
import AnimalSummary from "./components/AnimalSummary";
import ShareDetailSection from "./components/ShareDetailSection";
import AnimalEditModal from "./components/AnimalEditModal";

export default function HayvanDetayPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { animals, updateAnimalById, setAllAnimals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { payments, setAllPayments } = usePayments();

  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const animal = animals.find((item) => item.id === Number(id));

  const deleteSummary = useMemo(() => {
    if (!animal) return null;
    return analyzeAnimalDeletion(animal.id, animals, payments, customers);
  }, [animal, animals, payments, customers]);

  function handleAnimalSave(updated: Partial<AnimalItem>) {
    if (!animal) return;
    updateAnimalById(animal.id, updated);
    setShowEdit(false);
  }

  function handleDeleteAnimal() {
    if (!animal) return;

    const result = deleteAnimalCascade({
      animalId: animal.id,
      animals,
      customers,
      payments,
    });

    setAllAnimals(() => result.animals);
    setAllCustomers(result.customers);
    setAllPayments(result.payments);

    setShowDeleteConfirm(false);
    navigate("/panel/hayvanlar");
  }

  if (!animal) {
    return (
      <div className="min-h-screen bg-farm-cream flex items-center justify-center font-body">
        <div className="text-center px-4">
          <div className="w-12 h-12 flex items-center justify-center mx-auto text-farm-muted mb-3">
            <i className="ri-error-warning-line text-4xl" />
          </div>
          <p className="font-heading font-semibold text-farm-brown text-base mb-1">
            Hayvan bulunamadı
          </p>
          <p className="text-xs text-farm-muted mb-4">
            ID: {id} ile eşleşen kayıt yok.
          </p>
          <Link
            to="/panel/hayvanlar"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-farm-earth text-white text-xs font-semibold cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </div>
            Listeye Dön
          </Link>
        </div>
      </div>
    );
  }

  const eksikHisse = Math.max(0, animal.toplamHisse - animal.satirlanHisse);
  const kupeNo = getDisplayKupeNo(animal);
  const siparisNo = getDisplaySiparisNo(animal);
  const satisTipi = getAnimalSalesType(animal);

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-4xl mx-auto px-3 sm:px-5 py-4">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading font-bold text-base text-farm-brown">
                Hayvan Detayı
              </h1>

              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-farm-earth-pale text-farm-earth text-[10px] font-semibold">
                {animal.tur}
              </span>

              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-farm-cream border border-farm-border text-farm-muted text-[10px] font-semibold">
                {satisTipi}
              </span>

              {animal.hisseliSatisEtiketi && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-farm-border text-farm-brown text-[10px] font-semibold">
                  Hisseli Satış
                </span>
              )}

              {animal.disKesim && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-50 border border-sky-100 text-sky-700 text-[10px] font-semibold">
                  Dış Kesim
                </span>
              )}
            </div>

            <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-farm-muted">
              <span>
                Küpe No:{" "}
                <span className="font-semibold text-farm-brown">{kupeNo}</span>
              </span>
              <span>•</span>
              <span>
                Sipariş No:{" "}
                <span className="font-semibold text-farm-brown">{siparisNo}</span>
              </span>
              <span>•</span>
              <span>
                Kesim Günü:{" "}
                <span className="font-semibold text-farm-brown">
                  {animal.kesimGunu || "—"}
                </span>
              </span>
            </div>

            <p className="font-body text-xs text-farm-muted mt-1">
              {animal.toplamHisse} hisse ·
              <span className="text-farm-green"> {animal.satirlanHisse} satıldı</span>
              {eksikHisse > 0 && (
                <span className="text-farm-earth"> · {eksikHisse} boş</span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/panel/hayvanlar")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-farm-border bg-farm-warm text-farm-muted font-body text-xs font-semibold hover:bg-farm-cream hover:text-farm-brown transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xs" />
              </div>
              <span className="hidden sm:inline">Listeye Dön</span>
            </button>

            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-500 font-body text-xs font-semibold hover:bg-red-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-delete-bin-line text-xs" />
              </div>
              <span className="hidden sm:inline">Sil</span>
            </button>

            <button
              type="button"
              onClick={() => setShowEdit(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-farm-earth text-white font-body text-xs font-semibold hover:bg-farm-brown transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-edit-line text-xs" />
              </div>
              <span className="hidden sm:inline">Düzenle</span>
            </button>
          </div>
        </div>

        <AnimalSummary animal={animal} />

        <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
          <h2 className="font-heading font-semibold text-sm text-farm-brown">
            Hisse Kayıtları
            <span className="ml-2 font-body text-xs text-farm-muted font-normal">
              ({animal.hisseler.length} kayıt / {animal.toplamHisse} slot)
            </span>
          </h2>

          <div className="flex items-center gap-2 flex-wrap">
            {eksikHisse > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-farm-earth-pale text-farm-earth text-[10px] font-semibold whitespace-nowrap">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-alert-line text-[10px]" />
                </div>
                {eksikHisse} boş slot
              </span>
            )}

            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-farm-cream border border-farm-border text-farm-muted text-[10px] font-semibold whitespace-nowrap">
              Sıra: #{animal.siraNo}
            </span>
          </div>
        </div>

        <ShareDetailSection
          shares={animal.hisseler}
          toplamHisse={animal.toplamHisse}
          animalId={animal.id}
          defaultKesimUcreti={animal.defaultKesimUcreti}
          defaultParcalamaUcreti={animal.defaultParcalamaUcreti}
          defaultTasimaUcreti={animal.defaultTasimaUcreti}
        />

        <div className="h-6" />
      </div>

      {showEdit && (
        <AnimalEditModal
          animal={animal}
          onClose={() => setShowEdit(false)}
          onSave={handleAnimalSave}
        />
      )}

      {showDeleteConfirm && deleteSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-3">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>

            <p className="text-sm font-semibold text-gray-800 text-center mb-1">
              Hayvan silinsin mi?
            </p>
            <p className="text-xs text-gray-500 text-center mb-4">
              Bu işlem geri alınamaz.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-3 mb-4 text-xs text-gray-700 space-y-1">
              <p>
                <span className="font-semibold">Küpe No:</span> {kupeNo}
              </p>
              <p>
                <span className="font-semibold">Sipariş No:</span> {siparisNo}
              </p>
              <p>
                <span className="font-semibold">Silinecek hisse:</span>{" "}
                {deleteSummary.shareCount}
              </p>
              <p>
                <span className="font-semibold">Silinecek ödeme:</span>{" "}
                {deleteSummary.paymentCount}
              </p>
              <p>
                <span className="font-semibold">Etkilenecek müşteri:</span>{" "}
                {deleteSummary.affectedCustomerCount}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                type="button"
                className="flex-1 py-2 text-sm border-2 border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                İptal
              </button>

              <button
                onClick={handleDeleteAnimal}
                type="button"
                className="flex-1 py-2 text-sm bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}